import { DAY_PART_SELECTIONS } from 'constants/menu';

import { keyBy } from 'lodash-es';

import { IWithPricingProps, SanityMenuObject } from '@rbi-ctg/menu';
import { ILoyaltyRuleConditionEvaluation } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { ICartEntryType } from 'state/loyalty/in-restaurant-redemption';
import { OffersServiceMode } from 'state/loyalty/offers/types';
import {
  AppOffer,
  AppOfferListItem,
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EngineOfferListItem,
  LoyaltyEngineId,
  LoyaltyOffer,
  SanityOfferListItem,
  WithErrors,
  buildAppOfferGroup,
  isAppPersonalizedOffer,
  isAppPersonalizedOfferGroup,
  isEngineOfferGroup,
  isEngineSystemwideOffer,
  isSanityOfferGroup,
} from 'state/loyalty/types';
import { IValidDayPart } from 'state/menu/main-menu/types';
import { applyFiltersForAvailability } from 'state/menu/menu-options/apply-filters-for-availability';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { replaceKeyRegex } from 'utils/language';

import { buildServiceModeUnavailableMessageGenerator } from './loyalty-service-modes';
import { evaluateOfferGroupVisibility } from './offer-group-visibility';
import { buildOfferEvaluator } from './offer-list/offer-list-item-evaluator';
import { ReduceOffers } from './types';

/**
 * Deduplicates items by loyaltyEngineId while preserving all items without loyaltyEngineId.
 * This ensures that items like OfferGroups (which may not have loyaltyEngineId) are not
 * incorrectly filtered out, while still preventing duplicate offers.
 *
 * @param items - Array of items to deduplicate
 * @returns Deduplicated array maintaining original order
 */
const deduplicateByLoyaltyEngineId = (
  items: (AppPersonalizedOfferGroup | AppPersonalizedOffer | SanityOfferListItem)[]
): (AppPersonalizedOfferGroup | AppPersonalizedOffer | SanityOfferListItem)[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    const loyaltyEngineId = (item as any)?.loyaltyEngineId;
    // Always keep items without loyaltyEngineId (e.g., OfferGroups)
    if (!loyaltyEngineId) {
      return true;
    }
    // For items with loyaltyEngineId, only keep if not seen before
    if (seen.has(loyaltyEngineId)) {
      return false;
    }
    seen.add(loyaltyEngineId);
    return true;
  });
};

/**
 * Classify the offers that arrived from the CMS between two groups: those available to the current user and those that have already been applied to the cart.
 * Filter the offers that are not available and those that do not match the base offer models.
 */
export const reduceOffers: ReduceOffers = ({
  sanityOfferListItems,
  appPersonalizedOffers = [],
  appPersonalizedOfferGroups = [],
  engineOfferListItems,
  appliedOffers,
  inRestaurantRedemptionCart,
  checkLoyaltyOfferAvailability,
  getIncentiveUnavailableMessage,
  enableSortOffersForServiceMode,
  setOffersFeedbackMap,
  appliedFilters,
  selectedServiceMode,
  enableOfferGroups,
}) => {
  const hasAnyOfferListItems =
    sanityOfferListItems.length ||
    appPersonalizedOffers.length ||
    appPersonalizedOfferGroups.length;

  if (
    !hasAnyOfferListItems ||
    (!engineOfferListItems.length &&
      !appPersonalizedOffers.length &&
      !appPersonalizedOfferGroups.length)
  ) {
    return [[], []];
  }

  function isAppliedOffer(offer: AppOffer) {
    return !!appliedOffers.find(applied => applied.id === offer.loyaltyEngineId);
  }

  // Create a map that includes standalone offers, offer groups, and offers inside offer groups
  const engineOfferListItemsMap: Record<LoyaltyEngineId, EngineOfferListItem> = {};
  engineOfferListItems.forEach(item => {
    // Add the item itself (standalone offer or offer group)
    engineOfferListItemsMap[item.id] = item;

    // If it's an offer group, also add each offer inside it to the map
    if (isEngineOfferGroup(item)) {
      item.offers.forEach(offer => {
        engineOfferListItemsMap[offer.id] = offer;
      });
    }
  });

  const inRestaurantRedemptionMap = keyBy(inRestaurantRedemptionCart, cartEntry =>
    cartEntry.type === ICartEntryType.OFFER ? cartEntry.details.offer._id : cartEntry.referenceId
  );
  function isInRestaurantRedemption(offer: AppOffer) {
    return Boolean(inRestaurantRedemptionMap[offer.cmsId]);
  }

  const generateServiceModeUnavailableMessage = buildServiceModeUnavailableMessageGenerator(
    selectedServiceMode
  );

  function getEngineSystemwideOfferByLoyaltyEngineId(id: LoyaltyEngineId) {
    const engineOfferListItem = engineOfferListItemsMap[id];
    if (isEngineSystemwideOffer(engineOfferListItem)) {
      return engineOfferListItem;
    }
  }

  const evaluateOffer = buildOfferEvaluator({
    isAppliedOffer,
    isInRestaurantRedemption,
    getEngineSystemwideOfferByLoyaltyEngineId,
    getIncentiveUnavailableMessage,
    checkLoyaltyOfferAvailability,
    appliedFilters,
    generateServiceModeUnavailableMessage,
    enableSortOffersForServiceMode,
  });

  const topOfferListItems: AppOfferListItem[] = [];
  const centerOfferListItems: AppOfferListItem[] = [];
  const bottomOfferListItems: AppOfferListItem[] = [];
  const offersInCart: AppOffer[] = [];
  const offersFeedback = new Map<LoyaltyEngineId, ILoyaltyRuleConditionEvaluation[]>();
  const skipReasons = new Map<string, AppOffer[]>();

  // Build a combined list of items to process:
  // - Personalized Offer Groups first (if present)
  // - Standalone personalized offers next
  // - CMS items last
  // Dedupe offers by loyaltyEngineId, but preserve all items without loyaltyEngineId (like OfferGroups)
  const combinedOfferListItems = deduplicateByLoyaltyEngineId([
    ...appPersonalizedOfferGroups,
    ...appPersonalizedOffers,
    ...sanityOfferListItems,
  ]);

  combinedOfferListItems.forEach(item => {
    if (isAppPersonalizedOfferGroup(item)) {
      if (!enableOfferGroups) {
        return;
      }

      if (!item?.offers?.length) {
        return;
      }

      let hasOfferInCart = false;
      const visibleOffersInGroup = item.offers
        .map(offer => {
          const res = evaluateOffer(offer);
          if (res.skipReasons.length > 0) {
            res.skipReasons.forEach(({ reason }, key) => {
              const next = skipReasons.get(reason) || [];
              skipReasons.set(reason, next.concat(res.appOffer));
            });
            return null;
          }

          if (res.offerFeedback.size > 0) {
            res.offerFeedback.forEach((value, key) => {
              offersFeedback.set(key, value);
            });
          }

          if (res.destination === 'CART') {
            hasOfferInCart = true;
          }

          // Ensure we keep only personalized offers within personalized offer groups.
          return isAppPersonalizedOffer(res.appOffer) ? res.appOffer : null;
        })
        .filter(Boolean);

      const visibilityEvaluation = evaluateOfferGroupVisibility({
        offerGroup: item,
        visibleOffersInGroup: visibleOffersInGroup as AppOffer[],
        hasOfferInCart,
      });

      if (visibilityEvaluation.visible) {
        topOfferListItems.push({
          ...item,
          offers: visibleOffersInGroup as AppPersonalizedOffer[],
        });
      }

      return;
    }

    if (isSanityOfferGroup(item)) {
      if (!enableOfferGroups) {
        return;
      }

      // For groups, run the chain for each child offer as well
      if (!item?.offers?.length) {
        return;
      }

      const engineOfferGroup = engineOfferListItemsMap[item.loyaltyEngineId];
      if (!isEngineOfferGroup(engineOfferGroup)) {
        return;
      }

      let hasOfferInCart = false;

      const visibleOffersInGroup = item.offers
        .map(offer => {
          const res = evaluateOffer(offer);
          if (res.skipReasons.length > 0) {
            res.skipReasons.forEach(({ reason }, key) => {
              const next = skipReasons.get(reason) || [];
              skipReasons.set(reason, next.concat(res.appOffer));
            });
            return null;
          }

          // Merge offerFeedback if it has entries
          if (res.offerFeedback.size > 0) {
            res.offerFeedback.forEach((value, key) => {
              offersFeedback.set(key, value);
            });
          }

          if (res.destination === 'CART') {
            // Don't add the offer to the offersInCart list because applied
            // offers are added as standalone items
            hasOfferInCart = true;
          }
          return res.appOffer;
        })
        .filter(Boolean);

      const visibilityEvaluation = evaluateOfferGroupVisibility({
        offerGroup: item,
        visibleOffersInGroup,
        hasOfferInCart,
      });

      if (visibilityEvaluation.visible) {
        const appOfferGroup = buildAppOfferGroup(item, engineOfferGroup, visibleOffersInGroup);
        centerOfferListItems.push(appOfferGroup);
      }
      return;
    }

    // Standalone offer item
    const res = evaluateOffer(item);
    if (res.skipReasons.length > 0) {
      res.skipReasons.forEach(({ reason }, key) => {
        const next = skipReasons.get(reason) || [];
        skipReasons.set(reason, next.concat(res.appOffer));
      });
      return;
    }

    // Merge offerFeedback if it has entries
    if (res.offerFeedback.size > 0) {
      res.offerFeedback.forEach((value, key) => {
        offersFeedback.set(key, value);
      });
    }

    if (res.destination === 'TOP') {
      topOfferListItems.push(res.appOffer);
    } else if (res.destination === 'BOTTOM') {
      bottomOfferListItems.push(res.appOffer);
    } else if (res.destination === 'CENTER') {
      centerOfferListItems.push(res.appOffer);
    } else if (res.destination === 'CART') {
      offersInCart.push(res.appOffer);
    }
  });

  const offerListItems = [...topOfferListItems, ...centerOfferListItems, ...bottomOfferListItems];

  const offersFeedbackObject = Object.fromEntries(offersFeedback);
  setOffersFeedbackMap(offersFeedbackObject);

  return [offerListItems, offersInCart];
};

/**
 *
 * Given a string, will look for a key like {{KEY}} and will replace it with the given value.
 *
 * @param stringValue A string that might contain an specific key to be replaced
 * @param key the key you want to replaced with value
 * @param value the value you want to use to replace the KEY
 * @returns the string with the replaced key
 */
export const interpolateSanityKey = (stringValue: string, key: string, value: any) => {
  return stringValue.replace(replaceKeyRegex(key), value);
};

export const getServiceModeText = (
  // Remove the string `type`
  offerServiceMode: OffersServiceMode | string | undefined
) => {
  if (offerServiceMode === OffersServiceMode.DELIVERY_ONLY) {
    return t('deliveryOnly');
  } else if (offerServiceMode === OffersServiceMode.PICKUP_ONLY) {
    return t('pickUpOnly');
  }

  return undefined;
};

export const getOfferNextAvailableDayPart = (
  storeDayParts: ReadonlyArray<IValidDayPart>,
  offerDayParts: LoyaltyOffer['daypart'],
  options: { burgersForBreakfastEnabled?: boolean | null } = { burgersForBreakfastEnabled: false }
): IValidDayPart | undefined => {
  const incentiveDayPartsSet = new Set(offerDayParts?.map(part => part?.toLowerCase()));
  // Burgers for Breakfast
  if (
    incentiveDayPartsSet.size === 1 &&
    incentiveDayPartsSet.has(DAY_PART_SELECTIONS.BURGERS_FOR_BREAKFAST.toLowerCase()) &&
    !options.burgersForBreakfastEnabled
  ) {
    return;
  }
  return storeDayParts.find(
    dayPart => dayPart.key && incentiveDayPartsSet.has(dayPart.key.toLowerCase())
  );
};

export const incentiveHasEvaluationError = (
  incentive: WithErrors,
  errorCode: IncentiveEvaluationErrorCodes
) => {
  return (
    incentive.errors && incentive.errors.find((error: { code: string }) => error.code === errorCode)
  );
};

/**
 * Util to check the store availability of the menu entities related to the given offer for
 * 1M stores. It ignores day part availability
 */
export const isOneMenuOfferEntityAvailable = (
  offer: AppOffer,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode
) => {
  const isEntityAvailable = (entityId: string) => {
    const menuEntry = indexedMenu.find(entityId);
    // No day parts params are passed to avoid filtering because of day parts unavailability
    return !!menuEntry && isEntryAvailable(menuEntry, posServiceMode);
  };

  const [, ...sanitizedId] = offer.cmsId.split('-');
  return isEntityAvailable(offer.cmsId) || isEntityAvailable(sanitizedId.join('-'));
};

/**
 * Util to check the store availability of the menu entities related to the given offer for
 * Legacy stores. It ignores day part availability
 */
export const isLegacyMenuOfferEntityAvailable = (
  offer: AppOffer,
  prices: IWithPricingProps['prices'],
  vendor: IWithPricingProps['vendor']
) => {
  if (!offer.incentives) {
    return false;
  }

  // No day parts params are passed to avoid filtering because of day parts unavailability
  const isEntityAvailable = applyFiltersForAvailability({
    prices,
    vendor,
  });

  // For store availability, we only care about Combo, Picker or Items
  const menuObjectsIncentives = offer.incentives.filter(
    menuObject => !!menuObject && ['Combo', 'Picker', 'Item'].includes(menuObject.__typename || '')
  );

  return menuObjectsIncentives.every(menuObject =>
    isEntityAvailable((menuObject as unknown) as SanityMenuObject)
  );
};
